/*
 * PrdDraftProposalCard — v0.5 directive
 *
 * Orbit calls this when problem clarity is high + the natural beyond-
 * registry questions have rich answers + there's no marketplace match
 * (or the user picked Custom Build alongside one). The card asks:
 * "want me to turn this into a PRD now?"
 *
 * Replaces the 3-card DIY/Paid-Human/Explore routing screen entirely.
 *
 * Brand: brand-50 surface, hairline ring, no left-edge stripe, IBM
 * Plex Sans body + Serif italic accents, primary black CTA + ghost
 * secondary. The yellow accent is reserved for the editorial hook.
 */

import React from 'react';
import { FileText, Edit3, Plus, Store, ArrowUpRight } from 'lucide-react';

import type { DirectivePayload } from '../../lib/dossier-types';


export interface PrdDraftProposalCardProps {
  payload: DirectivePayload;
  /** User picked "Draft the PRD now" — parent commits via the
   *  draft_prd backend tool, then handles the workspace handoff. */
  onDraft: () => void;
  /** User wants to edit something before drafting. Parent typically
   *  returns the chat to the relevant ask_next. */
  onEdit?: () => void;
  /** User wants to add more context before drafting. Parent
   *  returns control to the chat. */
  onAddMore?: () => void;
  drafting?: boolean;
  /** Side-panel compact view: heading + buttons only. Hides summary, success,
   *  budget/cost detail (those show on the full-screen / intake view). */
  compact?: boolean;
}


export const PrdDraftProposalCard: React.FC<PrdDraftProposalCardProps> = ({
  payload, onDraft, onEdit, onAddMore, drafting, compact,
}) => {
  const title = payload.title || 'Your build';
  const summary = payload.summary || '';
  const integrations = payload.integrations || [];
  const successMetric = payload.success_metric || '';
  const draftLabel = payload.draft_label || 'Draft the PRD';
  const editLabel = payload.edit_label || 'Edit something first';
  const addMoreLabel = payload.add_more_label || 'Add more context';
  const budgetLabel = payload.budget_tier_label || null;
  const cost = payload.cost_estimate || null;
  // Natural marketplace match — only present when Orbit JUDGED a real fit.
  const match = (payload.marketplace_matches || [])[0] || null;
  // A floor-clearing candidate the judge rejected — shown as a transparent
  // "considered, not the fit" footnote (only when there's no genuine match to
  // show in its place), so a user who named a solution sees Orbit weighed it.
  const considered = payload.marketplace_considered || null;

  const prdCard = (
    <div className="aa-directive aa-directive--prd">
      <div className="aa-directive-prd-head">
        <span className="aa-directive-prd-eyebrow">
          <FileText size={11} strokeWidth={2.25} />
          Custom build · Draft PRD
        </span>
        <h3 className="aa-directive-prd-title">
          <em>{title}</em>
        </h3>
      </div>

      {/* Detail blocks — summary, success, budget/cost — only on the full
          (intake / expanded) view. The side-panel compact card is heading +
          buttons, so it stays small. Cost only ever shows when a real budget
          was given (backend sends cost_estimate=null otherwise — no fabricated
          "$15k–40k" band). */}
      {!compact && summary && (
        <p className="aa-directive-prd-summary">{summary}</p>
      )}

      {!compact && (integrations.length > 0 || successMetric) && (
        <div className="aa-directive-prd-meta">
          {integrations.length > 0 && (
            <div className="aa-directive-prd-meta-row">
              <span className="aa-directive-prd-meta-label">Connects to</span>
              <span className="aa-directive-prd-meta-value">
                {integrations.join(' · ')}
              </span>
            </div>
          )}
          {successMetric && (
            <div className="aa-directive-prd-meta-row">
              <span className="aa-directive-prd-meta-label">Success</span>
              <span className="aa-directive-prd-meta-value">{successMetric}</span>
            </div>
          )}
        </div>
      )}

      {!compact && (budgetLabel || cost) && (
        <div className="aa-directive-prd-meta aa-directive-prd-cost">
          {budgetLabel && (
            <div className="aa-directive-prd-meta-row">
              <span className="aa-directive-prd-meta-label">Budget</span>
              <span className="aa-directive-prd-meta-value">{budgetLabel}</span>
            </div>
          )}
          {cost?.build_band && (
            <div className="aa-directive-prd-meta-row">
              <span className="aa-directive-prd-meta-label">Est. build</span>
              <span className="aa-directive-prd-meta-value">
                {cost.build_band}
                {cost.monthly_run_band ? ` · ${cost.monthly_run_band}` : ''}
                <span className="aa-directive-prd-cost-note"> · preliminary</span>
              </span>
            </div>
          )}
        </div>
      )}

      <div className="aa-directive-actions">
        <button
          type="button"
          className="aa-directive-btn aa-directive-btn--primary aa-directive-btn--black"
          onClick={onDraft}
          disabled={!!drafting}
        >
          {drafting ? (
            <>
              <span className="aa-thinking-dot" /> Drafting…
            </>
          ) : (
            <>
              <FileText size={13} strokeWidth={2.25} />
              {draftLabel}
            </>
          )}
        </button>
        {onEdit && (
          <button
            type="button"
            className="aa-directive-btn aa-directive-btn--ghost"
            onClick={onEdit}
            disabled={!!drafting}
          >
            <Edit3 size={12} strokeWidth={2.25} />
            {editLabel}
          </button>
        )}
        {onAddMore && (
          <button
            type="button"
            className="aa-directive-btn aa-directive-btn--ghost"
            onClick={onAddMore}
            disabled={!!drafting}
          >
            <Plus size={12} strokeWidth={2.5} />
            {addMoreLabel}
          </button>
        )}
      </div>

      {/* Transparency footnote — a marketplace agent Orbit considered but
          judged not the right fit. Only shown when there's no genuine match
          taking its place, so the user who named it ("a chatbot") sees it was
          weighed and why a custom build won out. */}
      {!compact && !match && considered && (
        <p className="aa-directive-prd-considered">
          Considered <strong>{considered.name}</strong> from the marketplace
          {considered.why_not ? <> — not the fit here: {considered.why_not}</> : <> — not the right fit here.</>}
        </p>
      )}
    </div>
  );

  // No genuine marketplace fit → just the build card, full width.
  if (!match) return prdCard;

  // Orbit found a real off-the-shelf fit → show it beside the build option,
  // equal weight: buy what exists, or build exactly what you need.
  return (
    <div className="aa-prd-twocol">
      <article className="aa-directive aa-directive--shelf">
        <span className="aa-directive-prd-eyebrow">
          <Store size={11} strokeWidth={2.25} />
          {match.partial ? 'Closest on the shelf' : 'Already on the shelf'}
        </span>
        <h3 className="aa-shelf-name">{match.name}</h3>
        {!compact && match.tagline && <p className="aa-shelf-tagline">{match.tagline}</p>}
        {/* A partial match shows the honest caveat ("covers the core; you'd add
            X") so it's never presented as a perfect fit. A real fit shows why. */}
        {match.partial && match.caveat ? (
          <p className="aa-shelf-why"><em>Closest fit — {match.caveat}</em></p>
        ) : (!compact && (match.why_fit || (match.reasons && match.reasons.length > 0)) && (
          <p className="aa-shelf-why">
            <em>Fits because {match.why_fit || match.reasons[0]}</em>
          </p>
        ))}
        {!compact && (match.pricing || match.deploy_time) && (
          <div className="aa-shelf-pills">
            {match.deploy_time && <span className="aa-shelf-pill">Deploy {match.deploy_time}</span>}
            {match.pricing && <span className="aa-shelf-pill">{match.pricing}</span>}
          </div>
        )}
        <a
          className="aa-directive-btn aa-directive-btn--ghost aa-shelf-cta"
          href={`/marketplace/${match.slug}`}
          target="_blank"
          rel="noreferrer"
        >
          Use this
          <ArrowUpRight size={13} strokeWidth={2.25} />
        </a>
      </article>
      {prdCard}
    </div>
  );
};

export default PrdDraftProposalCard;
